import editorAction from '../actions/editorAction';
import { useCameraStateContext } from '../contexts/CameraContext';
import { useEffect } from 'react';
import useBlockFilter from './block/useBlockFilter';
import usePathFilter from './path/usePathFilter';
import { useBotStateContext } from '../contexts/BotContext';
import { useTreeStateContext } from '../contexts/TreeContext';
import {applyWillChange, disableWillChange} from '../utils/performanceEditor';
import binarySearchString from '../utils/binarySearchString';


// Helpers for set last position and get last position in the localStorage
const setLastPos = (id, value) => window.localStorage.setItem(`bot_${id}_pos`, JSON.stringify(value));
const getLastPos = (id) => {
    let value = window.localStorage.getItem(`bot_${id}_pos`);
    return value ? JSON.parse(value) : { x : 0, y: 0, scale : 1}
};

const scheduleVisibleBlocksRefresh = (panzone, refresh) => {
    if(panzone.visibleBlocksRefreshQueued) return;
    panzone.visibleBlocksRefreshQueued = true;
    Promise.resolve().then(() => {
        panzone.visibleBlocksRefreshQueued = false;
        refresh();
    });
};

// Hook to use the camera of the bot edtior
function useCamera() {

    const { botId } = useBotStateContext();
    const { panzone } = useCameraStateContext();
    const { filterBlocksVisible, blocks } = useBlockFilter();
    const { blocksVisible } = useTreeStateContext();
    const needsVisibleRefresh = blocks.length > 0 && blocksVisible.length == 0;
    const { filterPathsVisible } = usePathFilter()
    const { updateBlockVisibility } = editorAction();

    // Helper get position of the camera
    const getPosition = () => { 
        const {x, y, scale} = panzone.getTransform();
        return {x : Math.round(x), y : Math.round(y), scale : scale}
    }

    const refreshBlocksVisible = () => {
        if(botId == undefined) return;
        const { x, y, scale } = getPosition();
        setLastPos(botId, { x , y , scale })
        const blocksVisible = filterBlocksVisible({x, y, scale}, blocks);
        const pathsVisible = filterPathsVisible(blocksVisible);
        // Set Blocks and Paths visibility
        updateBlockVisibility(blocksVisible, pathsVisible);
    }

    const getPositionStart = () => {
        var id = window.location.hash.slice(1);
        if(!id) return getLastPos(botId);
        const index = binarySearchString(blocks, id, 'object_id');
        const block = blocks[index];
        if(!block) return getLastPos(botId);
        const scale = panzone.getTransform().scale;
        const x = -(block.x) * scale + 30;
        const y = -(block.y - window.innerHeight / 2.5) * scale;
        setTimeout(() => document.getElementById(block.object_id) ? document.getElementById(block.object_id).click() : null, 250);
        return { x, y, scale}
    }

    const triggerPanzone = () => {
        if(botId == undefined) return;
        const {x, y, scale} = getPositionStart();
        // If not zooomAbs at the beggining we can't move to
        panzone.zoomAbs(0, 0, scale);
        panzone.moveTo(x, y);
    }
    

    const onZoom = () => {
        // When zoom apply optimisation and check new blocks to show
        applyWillChange();
        refreshBlocksVisible()
        disableWillChange()
    }

    const onPan = refreshBlocksVisible

    const transformstart = () => { applyWillChange(); }

    const transformEnd = () => { disableWillChange(); }

    // this use effect need to have panzone in dependencies, to update the data of the 'panzone' values, we also need to relaunch the use effect if blocks.length change who caused may no rerendering
    useEffect( () => {

        if(panzone == null || panzone.disposed) {
            return;
        }

        if(!panzone.startPositionApplied && botId != undefined) {
            panzone.startPositionApplied = true;
            //need to trigger panzone if not done the blocks won't show at the beggining
            triggerPanzone();
        }
        scheduleVisibleBlocksRefresh(panzone, refreshBlocksVisible);

        panzone.on("zoom", onZoom);
        panzone.on("pan", onPan);  

        // The are custom event when transform property is set on some elements
        panzone.on('transformstart', transformstart)
        panzone.on('transformend', transformEnd)

        return () => {
            panzone.off("zoom", onZoom);
            panzone.off("pan", onPan);
            panzone.off('transformstart', transformstart)
            panzone.off('transformend', transformEnd)
        }

    }, [panzone, blocks.length, botId, needsVisibleRefresh])

    return { panzone, getPosition, refreshBlocksVisible }
}

export default useCamera